import { useState, useMemo, useEffect } from 'react';
import { Search } from 'lucide-react';
import { PageHeader, Input, Select, Button } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useAuditEvents, useAuditSummary } from '../hooks/useAuditLog';
import type { AuditEventType } from '../api/auditApi';
import { AuditSessionModal } from '../components/AuditSessionModal';
import { EventPill, Highlight } from '../components/auditFormatting';
import { EVENT_LABELS, formatEventTime, eventDetail } from '../components/auditFormat';
import styles from './AgencyAuditLogPage.module.css';

type TabKey = AuditEventType | 'all';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'all', label: 'All events' },
  { key: 'login', label: EVENT_LABELS.login },
  { key: 'page_view', label: EVENT_LABELS.page_view },
  { key: 'impersonation', label: EVENT_LABELS.impersonation },
  { key: 'login_failed', label: EVENT_LABELS.login_failed },
  { key: 'logout', label: EVENT_LABELS.logout },
];

const RANGE_OPTIONS = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

/** Debounce the search box so a query does not fire on every keystroke. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function AgencyAuditLogPage() {
  const [searchInput, setSearchInput] = useState('');
  const [tab, setTab] = useState<TabKey>('all');
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);

  const search = useDebounced(searchInput, 250);

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1);
  }, [search, tab, days]);

  const query = useMemo(() => ({ search, eventType: tab, days, page }), [search, tab, days, page]);

  const { data, isLoading } = useAuditEvents(query);
  const { data: summary } = useAuditSummary();

  const events = data?.events ?? [];
  const counts = data?.counts ?? {};

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Audit Log"
        subtitle="Every sign-in and every page opened, across the agency and all client practices."
      />

      <div className={patterns.statGrid4}>
        <div className={patterns.tile}>
          <span className={patterns.kicker}>Active users · 24h</span>
          <div className={patterns.cellPrimary}>{summary?.activeUsers24h ?? '—'}</div>
        </div>
        <div className={patterns.tile}>
          <span className={patterns.kicker}>Sessions · 24h</span>
          <div className={patterns.cellPrimary}>{summary?.sessions24h ?? '—'}</div>
        </div>
        <div className={patterns.tile}>
          <span className={patterns.kicker}>Page views · 24h</span>
          <div className={patterns.cellPrimary}>{summary?.pageViews24h ?? '—'}</div>
        </div>
        <div className={patterns.tile}>
          <span className={patterns.kicker}>Failed sign-ins · 24h</span>
          <div className={patterns.cellPrimary}>{summary?.failedLogins24h ?? '—'}</div>
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.search}>
          <Input
            placeholder="Search name, email, IP address or page path…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            leftIcon={<Search size={14} />}
            fullWidth
            aria-label="Search audit log"
          />
        </div>
        <div className={styles.rangeSelect}>
          <Select
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            options={RANGE_OPTIONS}
            aria-label="Time range"
          />
        </div>
      </div>

      <div className={patterns.filterGroup}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${patterns.filterPill} ${tab === t.key ? patterns.filterPillActive : ''}`}
            onClick={() => setTab(t.key)}
            aria-pressed={tab === t.key}
          >
            {t.label}
            <span className={styles.tabCount}>{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      <p className={styles.searchHint}>
        Counts reflect the current search, so they stay honest as you filter.
      </p>

      <div className={patterns.panel}>
        <div className={styles.tableScroll}>
          <table className={styles.eventTable}>
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Event</th>
                <th>Page / detail</th>
                <th>IP address</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className={styles.emptyRow}>
                    Loading…
                  </td>
                </tr>
              )}

              {!isLoading && events.length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.emptyRow}>
                    {search
                      ? `No events match “${search}” in this range.`
                      : 'No activity recorded in this range yet.'}
                  </td>
                </tr>
              )}

              {!isLoading &&
                events.map((event) => {
                  const { time, day } = formatEventTime(event.createdAt);
                  const detail = eventDetail(event);
                  const clickable = !!event.sessionId;

                  return (
                    <tr
                      key={event.id}
                      className={clickable ? styles.row : styles.rowStatic}
                      onClick={clickable ? () => setOpenSessionId(event.sessionId) : undefined}
                    >
                      <td className={styles.time}>
                        {time}
                        <span className={styles.timeDay}>{day}</span>
                      </td>
                      <td>
                        <span className={styles.userName}>
                          <Highlight text={event.userName ?? event.email} term={search} />
                          {event.isAgency && (
                            <>
                              {' '}
                              <span className={patterns.pillAccent}>AGENCY</span>
                            </>
                          )}
                        </span>
                        {/* Email is always shown, never swapped for the practice
                            name — search matches on it, so a hit has to be
                            visible in the row it matched. */}
                        <span className={styles.userEmail}>
                          <Highlight text={event.email} term={search} />
                          {event.practiceName && (
                            <>
                              {' · '}
                              <Highlight text={event.practiceName} term={search} />
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        <EventPill eventType={event.eventType} />
                      </td>
                      <td>
                        <span className={styles.pathLabel}>
                          <Highlight text={detail.primary} term={search} />
                        </span>
                        {detail.secondary && (
                          <span className={styles.path}>
                            <Highlight text={detail.secondary} term={search} />
                          </span>
                        )}
                      </td>
                      <td className={styles.ip}>
                        {event.ipAddress ? (
                          <Highlight text={event.ipAddress} term={search} />
                        ) : (
                          <span className={styles.muted}>—</span>
                        )}
                      </td>
                      <td>{event.location ?? <span className={styles.muted}>—</span>}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.totalPages > 1 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Page {data.page} of {data.totalPages} · {data.total} events
          </span>
          <div className={styles.paginationButtons}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={data.page <= 1}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage((p) => p + 1)}
              disabled={data.page >= data.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <p className={styles.geoNotice}>
        Events are kept for {summary?.retentionDays ?? 90} days, then pruned automatically. Location
        is resolved from the IP address and stays blank until a GeoIP database is configured.
      </p>

      <AuditSessionModal sessionId={openSessionId} onClose={() => setOpenSessionId(null)} />
    </div>
  );
}
